import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Equipamento, Equipamentos, InformacaoEquipamento } from 'app/interfaces/Equipamento';
import { Permissoes } from 'app/interfaces/permissoes';
import { EquipamentoService } from 'app/services/equipamento/equipamento.service';
import { PermissoesService } from 'app/services/permissoes/permissoes.service';
import { DialogService } from 'app/services/dialog/dialog.service';
import { LoadingComponent } from 'app/components/loading/loading.component';
import { FiltroClientesComponent } from 'app/components/filtro-clientes/filtro-clientes.component';
import { TipoEquipamento, CORES_TIPO_EQUIPAMENTO, TIPO_EQUIPAMENTO_OPTIONS } from 'app/constants/equipamento.constants';

@Component({
  selector: 'app-listar-equipamentos',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    TranslocoModule,
    LoadingComponent,
    FiltroClientesComponent
  ],
  templateUrl: './listar-equipamentos.component.html',
  styleUrl: './listar-equipamentos.component.css'
})
export class ListarEquipamentosComponent {
  public dataSource = new MatTableDataSource<Equipamento>();
  public loading = true;
  public tiposEquipamento = TIPO_EQUIPAMENTO_OPTIONS;
  public coresTipos = CORES_TIPO_EQUIPAMENTO;
  public equipamentos: Equipamentos = [];
  public quantidadeEquipamentosDisponiveis = 0;
  public quantidadePaginas = 0;
  public quantidadeItemPorPagina = 8;
  public listaPaginas: number[] = [];
  public filtroTipoEquipamento: TipoEquipamento | null = null;
  public filtroFrota = '';
  public displayedColumns: string[] = [
    'vc_frota',
    'en_tipo',
    'identificador_comunicador',
    'tipo_comunicador',
    'status'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  public permissoes: Permissoes.Acessos = {
    criar: false,
    editar: false,
    excluir: false,
    exibirAcoes: false
  };

  public readonly PERMISSAO_CRIAR = 'criar_equipamentos_premoplan';
  public readonly PERMISSAO_EDITAR = 'editar_equipamentos_premoplan';
  public readonly PERMISSAO_EXCLUIR = 'excluir_equipamentos_premoplan';

  constructor(
    private permissoesService: PermissoesService,
    public equipamentoService: EquipamentoService,
    private dialogService: DialogService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.verificarPermissoes();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.paginator.pageSize = this.quantidadeItemPorPagina;
  }

  public verificarPermissoes(): void {
    this.permissoes.criar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_CRIAR);
    this.permissoes.editar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EDITAR);
    this.permissoes.excluir = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EXCLUIR);
    this.permissoes.exibirAcoes = this.permissoes.editar || this.permissoes.excluir;

    if (this.permissoes.exibirAcoes && !this.displayedColumns.includes('acoes')) {
      this.displayedColumns.push('acoes');
    }
  }

  public buscarEquipamentos(clienteId: number): void {
    this.loading = true;
    this.dataSource.data = [];
    this.listaPaginas = [];

    this.equipamentoService
      .buscarTodosPorCliente(clienteId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (info: InformacaoEquipamento) => {
          this.equipamentos = info.equipamentos;
          this.dataSource.data = info.equipamentos;
          this.iniciarPaginator(this.equipamentos.length);
        },
        error: () => {
          this.dialogService.erro(
            this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.fetchError.title'),
            this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.fetchError.message')
          );
          this.dataSource.data = [];
        }
      });
  }

  public atualizarTabela(equipamentoAtualizado: Equipamento | number): void {
    if (typeof equipamentoAtualizado === 'number') {
      this.equipamentos = this.equipamentos.filter(e => e.sr_id !== equipamentoAtualizado);
    } else {
      const index = this.equipamentos.findIndex(e => e.sr_id === equipamentoAtualizado.sr_id);
      if (index !== -1) {
        this.equipamentos[index] = equipamentoAtualizado;
      }
    }
    this.dataSource.data = this.equipamentos;
  }

  private normalizarTexto(texto: string): string {
    return texto
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  public getCorTipoEquipamento(tipo: string | null | undefined): string {
    if (!tipo) {
      return '';
    }
    return this.coresTipos[tipo as TipoEquipamento] ?? '';
  }

  public async excluir(equipamentoId: number): Promise<void> {
    const confirmar = await this.dialogService.confirmacao(
      this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteConfirm.title'),
      this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteConfirm.message')
    );
    if (!confirmar) return;

    this.dialogService.loading(this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleting'));

    this.equipamentoService.deletar(equipamentoId).subscribe({
      next: async () => {
        await this.dialogService.sucesso(
          this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteSuccess.title'),
          this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteSuccess.message')
        );
        this.atualizarTabela(equipamentoId);
      },
      error: () => {
        this.dialogService.erro(
          this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteError.title'),
          this.translocoService.translate('COMPONENTS.EQUIPAMENTOS.dialogs.deleteError.message')
        );
      }
    });
  }

  public voltarPagina(): void {
    const pagina = this.paginator.pageIndex - 1;
    if (pagina < 0) return;
    this.mudarPagina(pagina);
  }

  public proximaPagina(): void {
    const pagina = this.paginator.pageIndex + 1;
    if (pagina > this.quantidadePaginas - 1) return;
    this.mudarPagina(pagina);
  }

  public mudarPagina(pagina: number): void {
    this.paginator.pageIndex = pagina;
    this.dataSource.paginator?.page.next({
      pageIndex: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      length: this.paginator.length
    });
  }

  public iniciarPaginator(total: number): void {
    this.mudarPagina(0);
    this.quantidadePaginas = Math.ceil(total / this.quantidadeItemPorPagina);
    this.listaPaginas = Array.from({ length: this.quantidadePaginas }, (_, i) => i + 1);
  }

  public filtrarEquipamentos(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;

    if (target instanceof HTMLInputElement) {
      this.filtroFrota = target.value;
    } else {
      this.filtroTipoEquipamento =
        target.value === 'null' ? null : (target.value as TipoEquipamento);
    }

    const frota = this.normalizarTexto(this.filtroFrota);
    const tipo = this.filtroTipoEquipamento;

    this.dataSource.filterPredicate = (data: Equipamento) => {
      const frotaMatch = this.normalizarTexto(data.vc_frota).includes(frota);
      const tipoMatch = !tipo || data.en_tipo === tipo;
      return frotaMatch && tipoMatch;
    };

    this.dataSource.filter = `${this.filtroFrota}|${this.filtroTipoEquipamento ?? ''}`;
    this.iniciarPaginator(this.dataSource.filteredData.length);
  }
}
